/**
 * File transfer over an established session.
 *
 * RustDesk models transfers as numbered jobs. Downloads are pulled with
 * FileTransferSendRequest and arrive as FileTransferBlock messages; uploads are
 * announced with FileTransferReceiveRequest and pushed as blocks we generate.
 *
 * Note this is a *separate connection type*: a session opened with
 * connType "file_transfer" carries files, a normal session carries the screen.
 */
import { decompress } from "fzstd";
import { encodeReadDir, encodeFileSendRequest, encodeFileReceiveRequest, encodeFileDirCreate, encodeFileRemoveDir, encodeFileRemoveFile, encodeFileRename, encodeFileCancel, encodeFileSendConfirm, encodeFileBlock, encodeFileDone, } from "./message.js";
/**
 * FileType in message.proto.
 *
 * The values are not contiguous and `DirDrive` is easy to miss: a Windows peer
 * sends drives as `DirDrive`, so leaving it out makes every drive look like a
 * file - shown with a file icon and not openable.
 */
export const FileType = {
    Dir: 0, DirLink: 2, DirDrive: 3, File: 4, FileLink: 5,
};
/** Upload chunk size. Matches the desktop client's block size. */
const BLOCK_SIZE = 128 * 1024;
/** Maps one entry as the peer sent it into what a browser shows. */
export function toEntry(e) {
    const t = e.entryType;
    return {
        name: e.name,
        isDir: t === FileType.Dir || t === FileType.DirLink || t === FileType.DirDrive,
        isLink: t === FileType.DirLink || t === FileType.FileLink,
        size: Number(e.size),
        modified: new Date(Number(e.modifiedTime) * 1000),
        hidden: e.isHidden,
    };
}
/** The root path. On Windows the peer only lists drives for exactly "/". */
export const ROOT_PATH = "/";
/** True when the path looks like a Windows one (a drive letter or a backslash). */
export function isWindowsPath(path) {
    return /^[A-Za-z]:/.test(path) || path.includes("\\");
}
export function separatorFor(path) {
    return isWindowsPath(path) ? "\\" : "/";
}
/**
 * Gives a bare drive letter its root separator.
 *
 * `C:` is *relative* to that drive's current directory, which for the service
 * RustDesk runs as is `C:\\Windows\\System32` - so asking for `C:` lists
 * System32, not the drive. `C:\\` is the drive itself. Every place a drive can
 * be used as a directory goes through here.
 */
export function asDirectory(path) {
    return /^[A-Za-z]:$/.test(path) ? `${path}\\` : path;
}
/** Joins a child onto a directory, respecting the platform's separator. */
export function joinPath(base, name) {
    if (!base || base === ROOT_PATH) {
        // From the drive list, "C:" becomes "C:\\" rather than "/C:".
        return isWindowsPath(name) || /^[A-Za-z]:$/.test(name)
            ? asDirectory(name)
            : ROOT_PATH + name;
    }
    const sep = separatorFor(base);
    // "C:" must become "C:\\Users", not "C:Users" - the latter is a path
    // relative to the drive's current directory, which is a different place.
    return base.endsWith(sep) ? base + name : base + sep + name;
}
/** The parent directory, stopping at the drive list rather than above it. */
export function parentPath(path) {
    if (!path || path === ROOT_PATH)
        return ROOT_PATH;
    const trimmed = path.replace(/[\\/]+$/, "");
    // "C:" has no parent directory; its parent is the list of drives.
    if (/^[A-Za-z]:$/.test(trimmed))
        return ROOT_PATH;
    const idx = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
    if (idx < 0)
        return ROOT_PATH;
    if (idx === 0)
        return ROOT_PATH; // "/usr" -> "/"
    const parent = trimmed.slice(0, idx);
    return asDirectory(parent) || ROOT_PATH;
}
/** Breadcrumb segments, each with the path to navigate to. */
export function breadcrumbs(path) {
    const crumbs = [
        { label: isWindowsPath(path) ? "Drives" : "/", path: ROOT_PATH },
    ];
    if (!path || path === ROOT_PATH)
        return crumbs;
    const windows = isWindowsPath(path);
    const sep = separatorFor(path);
    const trimmed = path.replace(/[\\/]+$/, "");
    const parts = trimmed.split(/[\\/]+/).filter(Boolean);
    // On Unix every crumb is an absolute path; dropping the leading slash would
    // make each one relative to wherever the peer happens to be.
    let acc = windows ? "" : "";
    for (const part of parts) {
        acc = acc ? acc + sep + part : windows ? part : ROOT_PATH + part;
        crumbs.push({ label: part, path: asDirectory(acc) });
    }
    return crumbs;
}
export class FileTransfer {
    #session;
    #nextId = 1;
    /**
     * The outstanding listing request, if any.
     *
     * Keyed on nothing rather than on the path: the peer normalises paths and
     * also pushes an unsolicited listing right after a file-transfer login, so
     * matching responses by the path we asked for silently drops them.
     */
    #pendingDir = null;
    #downloads = new Map();
    #uploads = new Map();
    #onProgress;
    /** Called for every listing, including ones the peer sends unprompted. */
    onDirectory;
    constructor(session, onProgress) {
        this.#session = session;
        this.#onProgress = onProgress;
    }
    #id() {
        return this.#nextId++;
    }
    /** Feed every session message here so transfers can advance. */
    handle(msg) {
        switch (msg.kind) {
            case "file_dir": {
                const dir = {
                    path: msg.path,
                    // Directories first, then by name, the way a file manager orders.
                    entries: msg.entries.map(toEntry).sort((a, b) => a.isDir === b.isDir ? a.name.localeCompare(b.name) : a.isDir ? -1 : 1),
                };
                const pending = this.#pendingDir;
                this.#pendingDir = null;
                pending?.resolve(dir);
                this.onDirectory?.(dir);
                return;
            }
            case "file_digest": {
                // The peer is telling us about a file it is about to send. Confirming
                // with offset 0 accepts it from the start; this is also where a resume
                // would specify a block offset.
                const dl = this.#downloads.get(msg.id);
                if (dl && !msg.isUpload) {
                    dl.total = Number(msg.fileSize);
                    this.#session.sendRaw(encodeFileSendConfirm(msg.id, msg.fileNum, { offsetBlk: 0 }));
                }
                return;
            }
            case "file_block": {
                const dl = this.#downloads.get(msg.id);
                if (!dl)
                    return;
                // Blocks are zstd-compressed when the peer decides it is worthwhile
                // (hbb_common::compress), regardless of anything we negotiate.
                let chunk = msg.data;
                if (msg.compressed) {
                    try {
                        chunk = decompress(chunk);
                    }
                    catch {
                        dl.reject(new Error("Could not decompress a block of this file"));
                        this.#downloads.delete(msg.id);
                        return;
                    }
                }
                dl.chunks.push(chunk);
                dl.received += chunk.length;
                this.#onProgress?.({
                    id: dl.id, name: dl.name, direction: "download",
                    transferred: dl.received, total: dl.total, done: false,
                });
                return;
            }
            case "file_done": {
                const dl = this.#downloads.get(msg.id);
                if (dl) {
                    this.#downloads.delete(msg.id);
                    this.#onProgress?.({
                        id: dl.id, name: dl.name, direction: "download",
                        transferred: dl.received, total: dl.total || dl.received, done: true,
                    });
                    const parts = dl.chunks.map((c) => {
                        // Copy into a plain ArrayBuffer: Blob cannot take a view that may
                        // be backed by shared memory.
                        const copy = new Uint8Array(c.length);
                        copy.set(c);
                        return copy;
                    });
                    dl.resolve(new Blob(parts));
                    return;
                }
                const up = this.#uploads.get(msg.id);
                if (up) {
                    this.#uploads.delete(msg.id);
                    this.#onProgress?.({
                        id: up.id, name: up.name, direction: "upload",
                        transferred: up.sent, total: up.file.size, done: true,
                    });
                    up.resolve();
                }
                return;
            }
            case "file_error": {
                const err = new Error(msg.error || "File transfer failed");
                const dl = this.#downloads.get(msg.id);
                if (dl) {
                    this.#downloads.delete(msg.id);
                    this.#onProgress?.({
                        id: dl.id, name: dl.name, direction: "download",
                        transferred: dl.received, total: dl.total, done: true, error: msg.error,
                    });
                    dl.reject(err);
                }
                const up = this.#uploads.get(msg.id);
                if (up) {
                    this.#uploads.delete(msg.id);
                    this.#onProgress?.({
                        id: up.id, name: up.name, direction: "upload",
                        transferred: up.sent, total: up.file.size, done: true, error: msg.error,
                    });
                    up.reject(err);
                }
                // A listing can fail too (a path that does not exist, or no
                // permission); fail the outstanding one.
                const pendingDir = this.#pendingDir;
                this.#pendingDir = null;
                pendingDir?.reject(err);
                return;
            }
            default:
                return;
        }
    }
    /**
     * Lists a remote directory. Use ROOT_PATH for drives (Windows) or `/`.
     * Resolves with the path the peer actually listed, which may be normalised.
     */
    listDir(path, includeHidden = false, timeoutMs = 20_000) {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                this.#pendingDir = null;
                reject(new Error("Timed out listing the remote directory"));
            }, timeoutMs);
            this.#pendingDir = {
                resolve: (dir) => { clearTimeout(timer); resolve(dir); },
                reject: (err) => { clearTimeout(timer); reject(err); },
            };
            // A bare drive letter would list that drive's current directory rather
            // than the drive, so it is normalised here as well as at the callers.
            this.#session.sendRaw(encodeReadDir(asDirectory(path), includeHidden));
        });
    }
    /** Downloads one remote file. */
    download(path, name = path.split(/[\\/]/).pop() ?? "download") {
        const id = this.#id();
        return new Promise((resolve, reject) => {
            this.#downloads.set(id, {
                id, name, chunks: [], received: 0, total: 0, resolve, reject,
            });
            this.#session.sendRaw(encodeFileSendRequest(id, path, 0, false));
        });
    }
    /**
     * Uploads a local file into `remoteDir`.
     *
     * The peer is told the size up front, then the content is streamed in
     * BLOCK_SIZE chunks and terminated with FileTransferDone.
     */
    async upload(remoteDir, file) {
        const id = this.#id();
        const done = new Promise((resolve, reject) => {
            this.#uploads.set(id, {
                id, name: file.name, file, sent: 0, blkId: 0, resolve, reject,
            });
        });
        /*
         * The verdict can arrive before this function gets as far as returning
         * `done`, and a promise that is rejected while nothing is attached to it
         * counts as unhandled - which ends the process. Attaching a handler now
         * settles that question; returning `done` below still delivers the
         * rejection to the caller, because a promise may have more than one.
         */
        done.catch(() => { });
        /*
         * Every path from here ends at `done`.
         *
         * The peer's verdict arrives on another channel while this loop is still
         * sending, so the loop can find its own bookkeeping already torn down by
         * a failure or a cancellation. It must not read that as success and
         * return: `done` has been rejected by then, and a rejection with nobody
         * waiting on it does not warn, it ends the process.
         */
        try {
            this.#session.sendRaw(encodeFileReceiveRequest(id, remoteDir, [{
                    entryType: FileType.File,
                    name: file.name,
                    size: file.size,
                    modifiedTime: Math.floor(file.lastModified / 1000),
                }], 0, file.size));
            const bytes = new Uint8Array(await file.arrayBuffer());
            for (let off = 0; off < bytes.length; off += BLOCK_SIZE) {
                const up = this.#uploads.get(id);
                // Gone: the peer failed it or it was cancelled, and `done` already
                // carries the reason. Stop sending and let the caller hear it.
                if (!up)
                    break;
                const chunk = bytes.subarray(off, Math.min(off + BLOCK_SIZE, bytes.length));
                this.#session.sendRaw(encodeFileBlock(id, 0, chunk, up.blkId++));
                up.sent += chunk.length;
                this.#onProgress?.({
                    id, name: file.name, direction: "upload",
                    transferred: up.sent, total: file.size, done: false,
                });
                // Yield so the UI stays responsive and the socket can drain.
                await new Promise((r) => setTimeout(r, 0));
            }
            const up = this.#uploads.get(id);
            if (up) {
                this.#session.sendRaw(encodeFileDone(id, 0));
                // An empty file gets no blocks, so nothing will come back for it.
                if (bytes.length === 0) {
                    this.#uploads.delete(id);
                    this.#onProgress?.({
                        id, name: file.name, direction: "upload", transferred: 0, total: 0, done: true,
                    });
                    up.resolve();
                }
            }
        }
        catch (err) {
            // A failure on this side - reading the file, or a closed socket. The
            // peer will never answer, so settle it here.
            const up = this.#uploads.get(id);
            this.#uploads.delete(id);
            up?.reject(err instanceof Error ? err : new Error(String(err)));
        }
        return done;
    }
    createDir(path) {
        this.#session.sendRaw(encodeFileDirCreate(this.#id(), path));
    }
    removeDir(path, recursive = true) {
        this.#session.sendRaw(encodeFileRemoveDir(this.#id(), path, recursive));
    }
    removeFile(path) {
        this.#session.sendRaw(encodeFileRemoveFile(this.#id(), path, 0));
    }
    rename(path, newName) {
        this.#session.sendRaw(encodeFileRename(this.#id(), path, newName));
    }
    /**
     * Abandons a transfer.
     *
     * Whoever is waiting on it has to be told, or they wait for an answer that
     * is never coming.
     */
    cancel(id) {
        this.#session.sendRaw(encodeFileCancel(id));
        const err = new Error("Transfer cancelled");
        const dl = this.#downloads.get(id);
        if (dl) {
            this.#downloads.delete(id);
            dl.reject(err);
        }
        const up = this.#uploads.get(id);
        if (up) {
            this.#uploads.delete(id);
            up.reject(err);
        }
    }
}
